/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1791849600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Tue Oct 13 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "openx",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to OpenX",
          "depth": 0,
          "id": "connect-to-openx"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Audiences",
          "depth": 1,
          "id": "audiences"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Behavior and limitations",
          "depth": 1,
          "id": "behavior-and-limitations"
        }, {
          "value": "Validate your setup",
          "depth": 1,
          "id": "validate-your-setup"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      a: "a",
      strong: "strong",
      code: "code",
      h3: "h3",
      h4: "h4",
      ul: "ul",
      li: "li"
    }, _provideComponents(), props.components), {FeatureFlag, MatchBoosterDestination, NoCommonErrors, SyncAlerts} = _components;
    if (!FeatureFlag) _missingMdxReference("FeatureFlag", true);
    if (!MatchBoosterDestination) _missingMdxReference("MatchBoosterDestination", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(FeatureFlag, {
        featureName: "The OpenX destination",
        contactMessage: "Hi, I'd like to enable the OpenX destination in my workspace."
      }), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "OpenX is a programmatic advertising exchange that connects advertisers with publisher inventory across web, mobile app, and connected TV (CTV). Sync your audiences to OpenX Direct Audiences as private segments in OpenXSelect, the OpenX platform where you build audiences from segments and target them through curated deals. If your model doesn't already contain identifiers OpenX can match, add them with Match Booster."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#audiences",
                children: _jsx(_components.strong, {
                  children: "Audiences"
                })
              })
            }), _jsx(_components.td, {
              children: "Create private OpenXSelect segments and refresh their membership on each sync run."
            }), _jsx(_components.td, {
              children: "All"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://docs.openx.com/openxselect/oxs-direct-audiences/",
                children: "Onboard Direct Audiences ↗"
              })
            })]
          })
        })]
      }), "\n", _jsx(_components.p, {
        children: "The linked OpenX guide requires an OpenXSelect login."
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-openx",
        children: "Connect to OpenX"
      }), "\n", _jsxs(_components.p, {
        children: ["The only setting is your ", _jsx(_components.strong, {
          children: "OpenXSelect organization ID"
        }), ", a UUID that OpenX account management provides, such as ", _jsx(_components.code, {
          children: "a0b1c2d3-e4f5-6789-abcd-ef0123456789"
        }), ". Hightouch delivers segments to the organization this ID identifies and doesn't check it, so confirm the ID with OpenX before your first sync."]
      }), "\n", _jsx(_components.p, {
        children: "You don't configure a bucket or credentials. Hightouch delivers files to a bucket OpenX set up for the Hightouch integration."
      }), "\n", _jsx(_components.p, {
        children: "The destination has no connection test. Your first sync run confirms that Hightouch can deliver to OpenX."
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsx(_components.h3, {
        id: "audiences",
        children: "Audiences"
      }), "\n", _jsxs(_components.p, {
        children: ["Each sync run delivers your full current audience to one OpenX segment. Select ", _jsx(_components.strong, {
          children: "Create a new OpenX segment"
        }), " when Hightouch should create the segment and set its name, description, and regions. Select ", _jsx(_components.strong, {
          children: "Use an existing OpenX segment"
        }), " when the segment already exists in OpenXSelect and Hightouch should only manage its membership."]
      }), "\n", _jsx(_components.p, {
        children: "OpenX doesn't accept data about users under 13, or users under 18 located in California. Exclude them from your model or audience."
      }), "\n", _jsx(_components.h4, {
        id: "create-a-new-segment",
        children: "Create a new segment"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch creates the segment with the ID ", _jsx(_components.code, {
          children: "hightouch-sync-<your sync's ID>"
        }), ". The segment is private to your OpenXSelect organization and isn't listed in the OpenX marketplace."]
      }), "\n", _jsxs(_components.p, {
        children: ["Enter a ", _jsx(_components.strong, {
          children: "Segment name"
        }), ", or leave it blank to use the name of your model or audience. You can customize the name with templating, like ", _jsx(_components.code, {
          children: "Audience_{model.id}"
        }), ". Use variables that stay the same across runs, such as ", _jsx(_components.code, {
          children: "{model.id}"
        }), ", ", _jsx(_components.code, {
          children: "{model.name}"
        }), ", and ", _jsx(_components.code, {
          children: "{sync.id}"
        }), ". Avoid ", _jsx(_components.code, {
          children: "{sync.run.id}"
        }), " and date and time variables, such as ", _jsx(_components.code, {
          children: "{DD}"
        }), ". Their values change between runs, which renames the segment."]
      }), "\n", _jsxs(_components.p, {
        children: ["Add a ", _jsx(_components.strong, {
          children: "Segment description"
        }), " to explain the audience in business terms. Hightouch sends the name and description to OpenX with every sync run."]
      }), "\n", _jsxs(_components.p, {
        children: ["Leave ", _jsx(_components.strong, {
          children: "Regions"
        }), " blank for a US-only audience. OpenX treats a blank region as US, and you can still set regions later."]
      }), "\n", _jsxs(_components.p, {
        children: ["When the audience includes people outside the US, select every region its users are in, including ", _jsx(_components.strong, {
          children: "United States (US)"
        }), " if some are in the US. The regions OpenX supports are ", _jsx(_components.strong, {
          children: "United States (US)"
        }), ", ", _jsx(_components.strong, {
          children: "Canada (CA)"
        }), ", ", _jsx(_components.strong, {
          children: "Europe (EU)"
        }), ", ", _jsx(_components.strong, {
          children: "Africa (AFR)"
        }), ", ", _jsx(_components.strong, {
          children: "Asia-Pacific (APAC)"
        }), ", and ", _jsx(_components.strong, {
          children: "Latin America (LATAM)"
        }), "."]
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Once Hightouch sends regions for a segment, later syncs can't change them. To\ncorrect a segment's regions, contact OpenX."
        })
      }), "\n", _jsx(_components.h4, {
        id: "use-an-existing-segment",
        children: "Use an existing segment"
      }), "\n", _jsx(_components.p, {
        children: "Enter the ID of the OpenX segment to sync to. The segment must already exist in the OpenXSelect organization this destination delivers to. Hightouch doesn't verify the ID, so if the segment's updated date in OpenXSelect doesn't change after OpenX's next processing run, confirm the ID."
      }), "\n", _jsx(_components.p, {
        children: "Hightouch only manages the segment's membership. It doesn't change the segment's name, description, or regions."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "Map one or more columns to OpenX ID types. Hightouch prepares each value in the format OpenX expects and drops values it can't deliver in that format:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "ID type"
            }), _jsx(_components.th, {
              children: "How Hightouch prepares values"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Email (SHA-256)"
              })
            }), _jsx(_components.td, {
              children: "Trims, lowercases, and SHA-256 hashes plaintext emails. Passes through values already hashed with SHA-256. Drops emails hashed another way, such as MD5 or SHA-1."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsxs(_components.td, {
              children: [_jsx(_components.strong, {
                children: "Mobile ad ID (IDFA or GAID)"
              }), ", ", _jsx(_components.strong, {
                children: "Android advertising ID (GAID)"
              }), ", ", _jsx(_components.strong, {
                children: "Apple advertising ID (IDFA)"
              }), ", and ", _jsx(_components.strong, {
                children: "CTV device ID"
              })]
            }), _jsx(_components.td, {
              children: "Lowercases each ID, as OpenX requires. Sends only UUID-formatted IDs and drops all-zero IDs."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "IPv4 address"
              })
            }), _jsxs(_components.td, {
              children: ["Sends only complete IPv4 addresses. Drops addresses ending in ", _jsx(_components.code, {
                children: ".0"
              }), " and IPv6 addresses."]
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch hashes any plaintext value mapped to ", _jsx(_components.strong, {
          children: "Email (SHA-256)"
        }), " without checking that it's an email, so map only email columns. It can't normalize values that are already hashed, so hash only lowercase, trimmed emails."]
      }), "\n", _jsx(_components.p, {
        children: "Columns that hold arrays expand to one identifier per value, so one row can contribute several device IDs. Hightouch removes duplicate identifiers from each row. All mapped ID types add members to the same OpenX segment, so one sync covers email, device, and IP matching."
      }), "\n", _jsx(_components.p, {
        children: "Hightouch skips rows that have no valid identifier for any mapped ID type. Skipped rows don't fail the sync run and aren't reported as rejected rows, so if the segment's reach in OpenXSelect is lower than you expect, check your ID mappings and values."
      }), "\n", _jsxs(_components.p, {
        children: ["Match Booster can add identifiers for ", _jsx(_components.strong, {
          children: "Email (SHA-256)"
        }), ", ", _jsx(_components.strong, {
          children: "Mobile ad ID (IDFA or GAID)"
        }), ", ", _jsx(_components.strong, {
          children: "Android advertising ID (GAID)"
        }), ", ", _jsx(_components.strong, {
          children: "Apple advertising ID (IDFA)"
        }), ", and ", _jsx(_components.strong, {
          children: "IPv4 address"
        }), ". It doesn't add ", _jsx(_components.strong, {
          children: "CTV device ID"
        }), " values. Match Booster can also use phone numbers to find matches, but Hightouch never sends phone numbers to OpenX."]
      }), "\n", _jsx(MatchBoosterDestination, {
        destinationName: "OpenX"
      }), "\n", _jsx(_components.h4, {
        id: "set-a-sync-schedule",
        children: "Set a sync schedule"
      }), "\n", _jsx(_components.p, {
        children: "Schedule OpenX syncs weekly. OpenX asks partners not to send daily full deliveries unless the audience changes significantly. OpenX processes new files once a day at midnight UTC, so a run's results appear in OpenXSelect after the next midnight UTC processing run."
      }), "\n", _jsxs(_components.p, {
        children: ["Each time OpenX processes new files, it rebuilds the segment from the deliveries it received over the previous 35 days, its default refresh window. If no new delivery arrives, the segment's membership stays as it is. Whatever ", _jsx(_components.a, {
          href: "/syncs/schedule-sync-ui",
          children: "schedule"
        }), " you set, run the sync at least once a month, as OpenX recommends, so the segment reflects your current audience."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["For Hightouch platform error codes related to OpenX, see ", _jsx(_components.a, {
            href: "/developer-tools/error-codes#other-destinations",
            children: "Error codes: Other\ndestinations"
          }), "."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "behavior-and-limitations",
        children: "Behavior and limitations"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "People who leave your audience stay in the OpenX segment until OpenX processes a delivery more than 35 days after the last one that included them. With a weekly schedule, that's about 35 to 42 days. OpenX doesn't support removing a person immediately."
        }), "\n", _jsx(_components.li, {
          children: "OpenX deletes a segment that receives no updates for 90 days, and OpenXSelect audiences that use it then fail to publish or refresh. Keep the sync scheduled while any deal depends on the segment."
        }), "\n", _jsxs(_components.li, {
          children: ["Pausing or deleting the sync doesn't remove the segment from OpenX. To remove it before the 90 days pass, archive it from the ", _jsx(_components.strong, {
            children: "Segments List"
          }), " in OpenXSelect or contact OpenX."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "validate-your-setup",
        children: "Validate your setup"
      }), "\n", _jsxs(_components.p, {
        children: ["After your first sync run, wait for OpenX's next midnight UTC processing run, then find the segment in the ", _jsx(_components.strong, {
          children: "Segments List"
        }), " in OpenXSelect:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["A new segment has the ID ", _jsx(_components.code, {
            children: "hightouch-sync-<your sync's ID>"
          }), " and the name you configured, or your model or audience name if you left it blank."]
        }), "\n", _jsx(_components.li, {
          children: "Confirm the segment's reach is in the range you expect. Its tooltip shows how many IDs OpenX received and how many it matched."
        }), "\n", _jsx(_components.li, {
          children: "If the segment doesn't appear, confirm your organization ID with OpenX account management."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "A segment isn't targetable on its own. Build an audience from it in OpenXSelect, activate the audience, and attach it to a deal. OpenX audience activation takes 6 to 10 hours."
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsxs(_components.p, {
        children: ["OpenX syncs write files to OpenX instead of calling an API, so the ", _jsx(_components.a, {
          href: "/syncs/debugger",
          children: "live debugger"
        }), " doesn't record their requests. When a run fails, check the error on the sync run."]
      }), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
